import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { storage } from '../utils/platform';
import { API_BASE } from '../config/api';

const CUISINE_EMOJI = {
  restaurant: '🍽️',
  glacier: '🍦',
  cafe: '☕',
  brasserie: '🍺',
  pizzeria: '🍕',
  japonais: '🍣',
  thailandais: '🇹🇭',
  'thaïlandais': '🇹🇭',
  healthy: '🥗',
  fastfood: '🍔',
  'fast-food': '🍔',
  boulangerie: '🥐',
  vins: '🍷',
  asiatique: '🍜',
  mexicain: '🌮',
  autre: '🍽️',
};

export default function QRCode() {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingType, setDownloadingType] = useState(null);
  const exportRef = useRef(null);
  const nomExportRef = useRef(null);
  const downloadingRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await storage.get('token');
      if (cancelled) return;
      if (!token) { navigate('/login'); return; }
      try {
        const r = await fetch(`${API_BASE}/api/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await r.json();
        if (cancelled) return;
        setRestaurant(data.restaurant);
        setLoading(false);
      } catch (_) {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    if (restaurant?.nom) {
      const previousTitle = document.title;
      document.title = `Revyo QR — ${restaurant.nom}`;
      return () => { document.title = previousTitle; };
    }
  }, [restaurant?.nom]);

  const qrUrl = restaurant ? `https://www.getrevyo.com/r/${restaurant.slug}` : '';
  const cuisineKey = (restaurant?.type_cuisine || '').toLowerCase().trim();
  const emoji = CUISINE_EMOJI[cuisineKey] || '🍽️';

  // Auto-shrink du nom sur l'export 9:16 pour rester sur 2 lignes max.
  useEffect(() => {
    if (!restaurant?.nom) return;
    let cancelled = false;
    const fit = async () => {
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch {}
      }
      if (cancelled) return;
      const el = nomExportRef.current;
      if (!el) return;
      const maxSize = 96;
      const minSize = 48;
      const lineHeight = 1.1;
      let size = maxSize;
      el.style.fontSize = size + 'px';
      while (size > minSize && el.scrollHeight > size * lineHeight * 2 + 2) {
        size -= 4;
        el.style.fontSize = size + 'px';
      }
    };
    fit();
    return () => { cancelled = true; };
  }, [restaurant?.nom, emoji]);

  const exportPNG = async ({ scale, suffix }) => {
    if (downloadingRef.current) return;
    if (!restaurant || !exportRef.current) return;
    downloadingRef.current = true;
    setDownloadingType(suffix);
    try {
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch {}
      }
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: null,
        scale,
        width: 1080,
        height: 1920,
      });
      await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(); return; }
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.rel = 'noopener';
          a.href = url;
          a.download = `revyo-qr-${restaurant.slug}-${suffix}.png`;
          setTimeout(() => URL.revokeObjectURL(url), 40000);
          setTimeout(() => a.dispatchEvent(new MouseEvent('click')), 0);
          resolve();
        }, 'image/png');
      });
    } catch (err) {
      console.error('Erreur download QR:', err);
      alert('Erreur lors du téléchargement du QR code');
    } finally {
      downloadingRef.current = false;
      setDownloadingType(null);
    }
  };

  const downloadSticker = () => exportPNG({ scale: 1, suffix: 'sticker' });
  const downloadBanderole = () => exportPNG({ scale: 2, suffix: 'banderole' });

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'Sora,sans-serif'}}>Chargement...</div>;

  return (
    <>
      <style>{css}</style>
      <div className="qr-wrap">
        <div className="qr-sidebar">
          <div className="sidebar-logo">Revyo</div>
          <nav className="sidebar-nav">
            <a className="nav-item" href="/dashboard">📊 Dashboard</a>
            <a className="nav-item active" href="/qrcode">📱 Mon QR Code</a>
            <a className="nav-item" href="/configuration">⚙️ Configuration</a>
          </nav>
        </div>

        <div className="qr-main">
          <div className="qr-header">
            <div className="qr-title">Mon QR Code</div>
            <div className="qr-sub">Imprimez ce QR code et posez-le sur vos tables</div>
          </div>

          <div className="qr-grid">

            {/* QR CODE — preview écran (inchangé visuellement) */}
            <div className="qr-card">
              <div className="card-title">QR Code — {restaurant?.nom}</div>
              <div className="qr-container">
                <div className="qr-logo-top">
                  <div className="logo-r">R</div>
                  <div className="logo-name">Revyo</div>
                </div>
                {restaurant && (
                  <div className="qr-whitebox">
                    <QRCodeSVG value={qrUrl} size={200} bgColor="#FFFFFF" fgColor="#000000" level="M" />
                  </div>
                )}
                <div className="qr-resto-name">{emoji} {restaurant?.nom}</div>
                <div className="qr-baseline">Votre avis compte pour nous ⭐</div>
              </div>
              <div className="qr-actions">
                <button type="button" className="btn-primary" onClick={downloadSticker} disabled={!!downloadingType}>
                  {downloadingType === 'sticker' ? '⏳ Génération...' : '📎 Télécharger sticker table'}
                </button>
                <button type="button" className="btn-primary" onClick={downloadBanderole} disabled={!!downloadingType}>
                  {downloadingType === 'banderole' ? '⏳ Génération...' : '📢 Télécharger banderole (grand format)'}
                </button>
                <button type="button" className="btn-ghost" onClick={() => window.print()} disabled={!!downloadingType}>
                  🖨️ Imprimer
                </button>
              </div>
            </div>

            {/* INFOS */}
            <div className="qr-info-col">
              <div className="info-card">
                <div className="card-title">🔗 Lien direct</div>
                <div className="link-box">
                  <span className="link-text">{qrUrl}</span>
                  <button className="copy-btn" onClick={() => {navigator.clipboard.writeText(qrUrl); alert('Lien copié !')}}>
                    Copier
                  </button>
                </div>
                <div className="link-sub">Partagez ce lien sur vos réseaux sociaux ou par SMS</div>
              </div>

              <div className="info-card">
                <div className="card-title">📍 Où placer le QR code ?</div>
                <div className="placement-list">
                  <div className="placement-item">
                    <div className="placement-icon">🧾</div>
                    <div>
                      <div className="placement-title">Sur l'addition</div>
                      <div className="placement-sub">Meilleur moment — le client est satisfait</div>
                    </div>
                  </div>
                  <div className="placement-item">
                    <div className="placement-icon">🪧</div>
                    <div>
                      <div className="placement-title">Sur la table</div>
                      <div className="placement-sub">Visible pendant tout le repas</div>
                    </div>
                  </div>
                  <div className="placement-item">
                    <div className="placement-icon">🚪</div>
                    <div>
                      <div className="placement-title">À la sortie</div>
                      <div className="placement-sub">Dernier contact avant de partir</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card stats-card">
                <div className="card-title">📊 Stats ce mois</div>
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">0</div>
                    <div className="stat-label">Scans</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">0</div>
                    <div className="stat-label">Avis générés</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">0%</div>
                    <div className="stat-label">Taux conversion</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Composant offscreen 9:16 utilisé pour le PNG (html2canvas) et l'impression */}
      <div ref={exportRef} className="qr-export" aria-hidden="true">
        <div className="export-logo">
          <div className="export-logo-r">R</div>
          <div className="export-logo-name">Revyo</div>
        </div>
        {restaurant && (
          <div className="export-whitebox">
            <QRCodeSVG value={qrUrl} size={560} bgColor="#FFFFFF" fgColor="#000000" level="M" />
          </div>
        )}
        <div className="export-nom" ref={nomExportRef}>
          {emoji} {restaurant?.nom}
        </div>
        <div className="export-message">Votre avis compte pour nous ⭐</div>
        <div className="export-instruction">📱 Scannez avec votre téléphone</div>
        <div className="export-compat">
          <span>✓ iPhone</span>
          <span>✓ Android</span>
        </div>
      </div>
    </>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Sora',sans-serif;background:#F5F3EE;}
.qr-wrap{display:flex;min-height:100vh;}
.qr-sidebar{width:220px;background:#1A1710;padding:24px 16px;display:flex;flex-direction:column;flex-shrink:0;}
.sidebar-logo{font-size:22px;font-weight:700;color:white;padding:8px 12px;margin-bottom:32px;}
.sidebar-nav{display:flex;flex-direction:column;gap:4px;}
.nav-item{padding:10px 12px;border-radius:10px;color:rgba(255,255,255,0.5);font-size:14px;font-weight:500;text-decoration:none;transition:all 0.15s;}
.nav-item:hover{background:rgba(255,255,255,0.08);color:white;}
.nav-item.active{background:rgba(200,66,10,0.25);color:#F0B49A;}
.qr-main{flex:1;padding:32px;}
.qr-header{margin-bottom:28px;}
.qr-title{font-size:24px;font-weight:700;letter-spacing:-0.5px;color:#1A1710;}
.qr-sub{font-size:14px;color:#6A6858;margin-top:4px;}
.qr-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
.qr-card,.info-card{background:white;border-radius:16px;padding:24px;border:1px solid #E8E4DA;}
.qr-info-col{display:flex;flex-direction:column;gap:16px;}
.card-title{font-size:14px;font-weight:700;color:#1A1710;margin-bottom:16px;}
.qr-container{display:flex;flex-direction:column;align-items:center;background:#F5F3EE;border-radius:12px;padding:28px 24px;margin-bottom:20px;}
.qr-logo-top{display:flex;align-items:center;gap:8px;margin-bottom:18px;}
.logo-r{width:28px;height:28px;background:#E8502A;border-radius:7px;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:14px;}
.logo-name{font-size:16px;font-weight:800;color:#1A1710;}
.qr-whitebox{background:#FFFFFF;padding:14px;border-radius:10px;display:flex;align-items:center;justify-content:center;line-height:0;}
.qr-resto-name{font-size:26px;font-weight:800;color:#1A1710;margin-top:18px;text-align:center;letter-spacing:-0.3px;}
.qr-baseline{font-size:13px;color:#6A6858;margin-top:6px;}
.qr-actions{display:flex;flex-direction:column;gap:10px;}
.btn-primary{padding:12px;background:#E8502A;color:white;border:none;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;}
.btn-ghost{padding:12px;background:white;color:#6A6858;border:1px solid #E8E4DA;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit;}
.link-box{display:flex;align-items:center;gap:8px;background:#F5F3EE;border-radius:8px;padding:10px 12px;margin-bottom:8px;}
.link-text{font-size:12px;color:#1A1710;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.copy-btn{padding:6px 12px;background:#E8502A;color:white;border:none;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;font-family:inherit;white-space:nowrap;}
.link-sub{font-size:12px;color:#ABA99F;}
.placement-list{display:flex;flex-direction:column;gap:12px;}
.placement-item{display:flex;align-items:center;gap:12px;}
.placement-icon{font-size:24px;width:40px;text-align:center;}
.placement-title{font-size:13px;font-weight:600;color:#1A1710;}
.placement-sub{font-size:11px;color:#ABA99F;margin-top:2px;}
.stats-card{}
.stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;}
.stat-item{text-align:center;background:#F5F3EE;border-radius:10px;padding:14px 8px;}
.stat-value{font-size:22px;font-weight:700;color:#1A1710;}
.stat-label{font-size:11px;color:#6A6858;margin-top:4px;}
.btn-primary:disabled,.btn-ghost:disabled{opacity:0.6;cursor:not-allowed;}

/* === EXPORT 9:16 (offscreen à l'écran, visible en impression) === */
.qr-export{
  position:fixed;left:-99999px;top:0;
  width:1080px;height:1920px;
  background:transparent;
  pointer-events:none;
  font-family:'Sora','Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif;
  display:flex;flex-direction:column;align-items:center;
  padding:110px 60px 80px 60px;
  box-sizing:border-box;
}
.export-logo{display:flex;align-items:center;gap:14px;}
.export-logo-r{width:52px;height:52px;background:#E8502A;border-radius:13px;color:white;font-weight:800;font-size:26px;display:flex;align-items:center;justify-content:center;line-height:1;}
.export-logo-name{font-size:44px;font-weight:800;color:#1A1710;letter-spacing:-0.5px;line-height:1;}
.export-whitebox{background:#FFFFFF;border-radius:24px;padding:40px;margin-top:130px;display:flex;align-items:center;justify-content:center;line-height:0;}
.export-nom{margin-top:110px;font-weight:800;color:#000000;text-align:center;letter-spacing:-0.5px;line-height:1.1;max-width:960px;word-wrap:break-word;overflow-wrap:break-word;font-size:96px;}
.export-message{margin-top:60px;font-size:54px;font-weight:500;color:#000000;text-align:center;line-height:1.2;max-width:960px;}
.export-instruction{margin-top:80px;font-size:32px;font-weight:500;color:#555555;text-align:center;}
.export-compat{margin-top:40px;font-size:24px;font-weight:400;color:#888888;letter-spacing:1px;display:flex;justify-content:center;gap:40px;}

@page{margin:0;size:1080px 1920px;}
@media print{
  html,body{background:transparent!important;}
  .qr-wrap{display:none!important;}
  .qr-export{
    position:static!important;
    left:auto!important;top:auto!important;
    margin:0 auto!important;
  }
  .qr-export,.qr-export *{
    -webkit-print-color-adjust:exact!important;
    print-color-adjust:exact!important;
    color-adjust:exact!important;
  }
}
`;